/**
 * Story Beats client — typed wrappers for the admin User-Insights beat
 * surface: captured Story Beats (Scene + Reflection + multi-card cross-tags)
 * and per-card beat-based coverage.
 *
 * These hit `/api/v1/admin/users/:identifier/*`, which is gated by the
 * /admin/* clerkAuth + adminAuth chain — an observability surface used under
 * super-admin "Acting as <user>" impersonation. The `identifier` is the
 * target user's UUID (or, when not impersonating, the admin's own id).
 *
 * Every query key includes the identifier so a "test as user" switch is a
 * cache miss (no cross-user stale data) — the same load-bearing pattern as
 * `memory-archive.ts`.
 */
import { apiClient } from '@/services/api-client';

// ─── Beat shapes (mirror the API jsonb contract) ────────────────────────────

export interface AttrValue {
  value: string;
  /** Verbatim provenance quote from the user's words. */
  quote: string;
}

export interface BeatScene {
  present: boolean;
  characters: AttrValue[];
  time: AttrValue | null;
  place: AttrValue | null;
  action: AttrValue | null;
  missing_attributes: string[];
}

export interface BeatReflection {
  present: boolean;
  action: AttrValue | null;
  catalyst: AttrValue | null;
  emotion_then: AttrValue[];
  emotion_now: AttrValue[];
  missing_attributes: string[];
}

export interface MappedCard {
  /** Human-readable card id, e.g. "C002". */
  cardId: string;
  /** story_cards.id (UUID). */
  cardUuid: string;
  title: string;
  /** 0..1 Curator-assigned relevance; the primary card is 1.0. */
  relevanceScore: number;
  isPrimary: boolean;
}

export interface StoryBeat {
  id: string;
  sessionId: string;
  primaryCardId: string;
  card: { id: string; cardId: string; title: string } | null;
  /** Primary + every cross-tag this beat maps to (primary-first). */
  mappedCards: MappedCard[];
  scene: BeatScene | null;
  reflection: BeatReflection | null;
  sceneScore: number;
  reflectionScore: number;
  beatQualityScore: number;
  followupSuggested: string | null;
  source: string;
  createdAt: string;
}

export interface StoryBeatsResult {
  items: StoryBeat[];
  total: number;
  limit: number;
}

export interface StoryBeatsFilters {
  /** Filter to one primary card (story_cards UUID). */
  cardId?: string | null;
  limit?: number;
  offset?: number;
}

const ROOT = ['admin-insights'] as const;
const tenant = (identifier?: string | null) => identifier ?? 'none';

export function storyBeatsQueryKey(identifier: string | null, filters: StoryBeatsFilters) {
  return [
    ...ROOT,
    'story-beats',
    tenant(identifier),
    {
      cardId: filters.cardId ?? null,
      limit: filters.limit ?? 50,
      offset: filters.offset ?? 0,
    },
  ] as const;
}

export async function fetchStoryBeats(
  identifier: string,
  filters: StoryBeatsFilters = {}
): Promise<StoryBeatsResult> {
  const params: Record<string, string> = {};
  if (filters.cardId) params.cardId = filters.cardId;
  if (filters.limit != null) params.limit = String(filters.limit);
  if (filters.offset) params.offset = String(filters.offset);

  const response = await apiClient.get<StoryBeat[]>(
    `/api/v1/admin/users/${encodeURIComponent(identifier)}/story-beats`,
    Object.keys(params).length > 0 ? params : undefined
  );
  if (!response.success) {
    throw new Error(response.error?.message ?? 'Failed to load story beats');
  }
  return {
    items: (response.data as unknown as StoryBeat[]) ?? [],
    total: response.meta?.total ?? 0,
    limit: response.meta?.limit ?? filters.limit ?? 50,
  };
}

// ─── Beat-based coverage ─────────────────────────────────────────────────────

export interface BeatCoverageCard {
  cardId: string;
  cardUuid: string;
  title: string;
  depth: number;
  parentCardId: string | null;
  beats: number;
  coveragePct: number;
}

export interface BeatCoverageResult {
  /** "Good coverage" beat target (the N in `beats: x/N`). */
  target: number;
  relevanceFloor: number;
  cards: BeatCoverageCard[];
}

export function beatCoverageQueryKey(identifier: string | null) {
  return [...ROOT, 'beat-coverage', tenant(identifier)] as const;
}

export async function fetchBeatCoverage(identifier: string): Promise<BeatCoverageResult> {
  const response = await apiClient.get<BeatCoverageResult>(
    `/api/v1/admin/users/${encodeURIComponent(identifier)}/beat-coverage`
  );
  if (!response.success) {
    throw new Error(response.error?.message ?? 'Failed to load beat coverage');
  }
  return (
    (response.data as unknown as BeatCoverageResult) ?? {
      target: 3,
      relevanceFloor: 0.5,
      cards: [],
    }
  );
}
